<template>
  <div>
    <b-modal
      v-model="bulkJobModalVisible"
      centered
      hide-header
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
      body-class="p-0"
      content-class="border-0 shadow"
    >
      <div class="text-center py-4 px-3">
        <b-spinner
          type="grow"
          variant="primary"
          style="width: 3rem; height: 3rem"
          label="Spinning"
        />
        <p class="mt-2 mb-0 font-weight-medium">
          {{ $t("Processing campus... Please wait.") }}
        </p>
      </div>
    </b-modal>

    <div class="m-2">
      <b-container>
        <b-row align-h="center" v-if="detailLoading">
          <b-spinner
            class="m-5"
            style="width: 3rem; height: 3rem"
            type="grow"
            variant="primary"
            label="Spinning"
          />
        </b-row>

        <template v-else>
          <b-row>
            <b-col cols="2"></b-col>
            <b-col cols="8">
              <h1>
                {{ campus.id == 0 ? $t("Add Campus") : $t("Edit Campus") }}
              </h1>
            </b-col>
            <b-col cols="2"></b-col>
          </b-row>

          <b-row>
            <b-col cols="2"></b-col>
            <b-col md="8">
              <b-card>
                <b-tabs content-class="mt-1" fill>
                  <!-- ─────────────────────────────────────────
                     TAB 1 · Basic Info
                ───────────────────────────────────────── -->
                  <b-tab :title="$t('Basic Info')" active>
                    <!-- Logo -->
                    <b-row align-h="center" class="mb-1">
                      <b-col cols="auto">
                        <b-form-group
                          class="text-center"
                          ref="picture"
                          label=" "
                        >
                          <b-media no-body>
                            <div v-if="logoloading == true">
                              <b-spinner
                                variant="primary"
                                type="grow"
                                class="m-5"
                                label="Spinning"
                              />
                            </div>
                            <b-media-aside>
                              <b-link>
                                <b-img
                                  ref="previewEl"
                                  rounded
                                  src="https://cdn.cloudious.net/file-1776666463641-10693955.png"
                                  @click="$refs.fileProfile.click()"
                                  v-if="logoloading == false"
                                  thumbnail
                                  fluid
                                  height="120px"
                                  width="120px"
                                />
                              </b-link>
                              <b-link v-if="logoloading == 'loaded'">
                                <b-button
                                  class="position-absolute mt-3 ml-3 rounded-circle btn-icon"
                                  variant="gradient-danger"
                                  @click="deleteLogo"
                                >
                                  <feather-icon icon="Trash2Icon" />
                                </b-button>
                                <b-img
                                  rounded
                                  :src="campus.logo"
                                  height="120px"
                                  width="120px"
                                />
                              </b-link>
                              <input
                                type="file"
                                id="fileProfile"
                                hidden
                                ref="fileProfile"
                                @change="selectLogo"
                                accept="image/*"
                              />
                            </b-media-aside>
                          </b-media>
                        </b-form-group>
                      </b-col>
                    </b-row>

                    <b-form @submit.prevent>
                      <b-row>
                        <b-col md="6">
                          <b-form-group
                            :label="$t('Institute Name') + ' *'"
                            :invalid-feedback="$t('Institute name is required')"
                          >
                            <b-form-input
                              ref="instituteName"
                              :placeholder="$t('Enter institute name')"
                              v-model="detailObj.instituteName"
                              @focusout="checkField('instituteName')"
                            />
                          </b-form-group>
                        </b-col>

                        <b-col md="6">
                          <b-form-group
                            :label="$t('Branch Name') + ' *'"
                            :invalid-feedback="$t('Branch name is required')"
                          >
                            <b-form-input
                              ref="branch"
                              :placeholder="$t('Enter branch name')"
                              v-model="detailObj.branch"
                              @focusout="checkField('branch')"
                            />
                          </b-form-group>
                        </b-col>

                        <b-col md="6">
                          <b-form-group
                            :label="$t('Branch Code') + ' *'"
                            :invalid-feedback="$t('Branch code is required')"
                          >
                            <b-form-input
                              ref="branchCode"
                              v-model="detailObj.branchCode"
                              :placeholder="$t('Enter branch code')"
                              @focusout="checkField('branchCode')"
                            />
                          </b-form-group>
                        </b-col>

                        <b-col md="6">
                          <b-form-group :label="$t('Year of Establishment')">
                            <b-form-input
                              type="number"
                              :placeholder="$t('e.g. 2010')"
                              v-model.number="detailObj.yearOfEstablishment"
                            />
                          </b-form-group>
                        </b-col>

                        <b-col md="12" v-if="isIussDomain">
                          <b-form-group :label="$t('Companies')">
                            <v-select
                              v-model="selectedCompanyIds"
                              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                              :options="companySelectOptions"
                              :reduce="(op) => op.id"
                              label="label"
                              multiple
                              :placeholder="
                                $t('Link campus to companies (optional)')
                              "
                            />
                            <small class="text-muted">{{
                              $t(
                                "IUSS users only see campuses that share a company with their campus."
                              )
                            }}</small>
                          </b-form-group>
                        </b-col>

                        <b-col md="6">
                          <b-form-group :label="$t('Primary Contact') + ' *'">
                            <vue-tel-input
                              v-model="detailObj.primaryContact"
                              :inputOptions="{
                                placeholder: $t('Enter primary contact'),
                              }"
                              :dropdownOptions="dropOptions"
                              :defaultCountry="$store.state.countryCode"
                              @input="checkPrimaryContact"
                            />
                            <small
                              class="mr-1 text-danger"
                              v-if="primaryContactError"
                              >{{
                                $t(
                                  "Primary contact is required and must be valid"
                                )
                              }}</small
                            >
                          </b-form-group>
                        </b-col>

                        <b-col md="6">
                          <b-form-group :label="$t('WhatsApp Number') + ' *'">
                            <vue-tel-input
                              v-model="detailObj.whatsappNumber"
                              :inputOptions="{
                                placeholder: $t('Enter WhatsApp number'),
                              }"
                              :dropdownOptions="dropOptions"
                              :defaultCountry="$store.state.countryCode"
                              @input="checkWhatsappNumber"
                            />
                            <small
                              class="mr-1 text-danger"
                              v-if="whatsappError"
                              >{{
                                $t(
                                  "WhatsApp number is required and must be valid"
                                )
                              }}</small
                            >
                          </b-form-group>
                        </b-col>

                        <b-col md="6">
                          <b-form-group
                            :label="$t('Email') + ' *'"
                            :invalid-feedback="$t('Valid email is required')"
                          >
                            <b-form-input
                              ref="email"
                              type="email"
                              placeholder="institute@example.com"
                              v-model="detailObj.email"
                              @focusout="checkEmail"
                            />
                          </b-form-group>
                        </b-col>

                        <b-col md="6">
                          <b-form-group :label="$t('Website')">
                            <b-form-input
                              ref="website"
                              placeholder="www.institute.com"
                              v-model="campus.website"
                            />
                          </b-form-group>
                        </b-col>

                        <b-col md="12">
                          <b-form-group
                            :label="$t('Address')"
                            :invalid-feedback="$t('Address is required')"
                          >
                            <b-form-input
                              ref="address"
                              :placeholder="$t('Enter address')"
                              v-model="detailObj.address"
                            />
                          </b-form-group>
                        </b-col>
                      </b-row>
                    </b-form>
                  </b-tab>

                  <!-- ─────────────────────────────────────────
                     TAB 2 · Point of Contact
                ───────────────────────────────────────── -->
                  <b-tab :title="$t('Point of Contact')">
                    <b-form @submit.prevent>
                      <b-row>
                        <b-col md="6">
                          <b-form-group
                            :label="$t('POC Name') + ' *'"
                            :invalid-feedback="$t('POC name is required')"
                          >
                            <b-form-input
                              ref="pocName"
                              :placeholder="$t('Enter POC name')"
                              v-model="detailObj.pocName"
                              @focusout="checkField('pocName')"
                            />
                          </b-form-group>
                        </b-col>

                        <b-col md="6">
                          <b-form-group :label="$t('POC Designation')">
                            <b-form-input
                              :placeholder="$t('Enter designation')"
                              v-model="detailObj.pocDesignation"
                            />
                          </b-form-group>
                        </b-col>

                        <b-col md="6">
                          <b-form-group
                            :label="$t('POC Contact Number') + ' *'"
                          >
                            <vue-tel-input
                              v-model="detailObj.pocContactNumber"
                              :inputOptions="{
                                placeholder: $t('Enter contact number'),
                              }"
                              :dropdownOptions="dropOptions"
                              :defaultCountry="$store.state.countryCode"
                              @input="checkPocContactNumber"
                            />
                            <small
                              class="mr-1 text-danger"
                              v-if="pocContactNumberError"
                              >{{
                                $t(
                                  "POC contact number is required and must be valid"
                                )
                              }}</small
                            >
                          </b-form-group>
                        </b-col>

                        <b-col md="6">
                          <b-form-group :label="$t('POC Email')">
                            <b-form-input
                              type="email"
                              placeholder="poc@example.com"
                              v-model="detailObj.pocEmail"
                            />
                          </b-form-group>
                        </b-col>

                        <b-col md="6">
                          <b-form-group :label="$t('POC CNIC')">
                            <b-form-input
                              placeholder="XXXXX-XXXXXXX-X"
                              v-model="detailObj.pocCnic"
                            />
                          </b-form-group>
                        </b-col>

                        <b-col md="6">
                          <b-form-group :label="$t('POC Address')">
                            <b-form-input
                              :placeholder="$t('Enter POC address')"
                              v-model="detailObj.pocAddress"
                            />
                          </b-form-group>
                        </b-col>

                        <!-- POC Picture -->
                        <b-col md="12">
                          <b-form-group :label="$t('POC Picture')">
                            <div class="d-flex align-items-center">
                              <b-img
                                v-if="detailObj.pocPicture"
                                :src="detailObj.pocPicture"
                                rounded="circle"
                                height="60px"
                                width="60px"
                                class="mr-1"
                              />
                              <b-button
                                variant="outline-primary"
                                size="sm"
                                @click="$refs.pocPicInput.click()"
                                :disabled="pocPicLoading"
                              >
                                <b-spinner
                                  v-if="pocPicLoading"
                                  small
                                  type="grow"
                                  class="mr-50"
                                />
                                <feather-icon
                                  v-else
                                  icon="UploadIcon"
                                  class="mr-50"
                                />
                                {{
                                  detailObj.pocPicture
                                    ? $t("Change")
                                    : $t("Upload")
                                }}
                              </b-button>
                              <b-button
                                v-if="detailObj.pocPicture"
                                variant="outline-danger"
                                size="sm"
                                class="ml-1"
                                @click="detailObj.pocPicture = ''"
                              >
                                <feather-icon icon="Trash2Icon" />
                              </b-button>
                              <input
                                ref="pocPicInput"
                                type="file"
                                hidden
                                accept="image/*"
                                @change="uploadPocPic"
                              />
                            </div>
                          </b-form-group>
                        </b-col>
                      </b-row>
                    </b-form>
                  </b-tab>

                  <!-- ─────────────────────────────────────────
                     TAB 3 · Programs & Capacity
                ───────────────────────────────────────── -->
                  <b-tab :title="$t('Programs & Capacity')">
                    <b-form @submit.prevent>
                      <b-row>
                        <b-col md="12">
                          <b-form-group :label="$t('Programs Offered')">
                            <b-row>
                              <b-col
                                v-for="prog in programOptions"
                                :key="prog"
                                md="3"
                                sm="6"
                                cols="6"
                                class="mb-1"
                              >
                                <b-form-checkbox
                                  v-model="selectedPrograms"
                                  :value="prog"
                                >
                                  {{ prog }}
                                </b-form-checkbox>
                              </b-col>
                            </b-row>
                            <b-form-group :label="$t('Others')" class="mt-1">
                              <b-form-input
                                v-model="othersProgram"
                                :placeholder="
                                  $t('Specify other programs (comma separated)')
                                "
                              />
                            </b-form-group>
                          </b-form-group>
                        </b-col>

                        <b-col md="6">
                          <b-form-group :label="$t('Student Capacity')">
                            <b-form-input
                              type="number"
                              :placeholder="$t('Enter student capacity')"
                              v-model.number="detailObj.studentCapacity"
                            />
                          </b-form-group>
                        </b-col>

                        <b-col md="6">
                          <b-form-group :label="$t('Avg Class Size')">
                            <b-form-input
                              type="number"
                              :placeholder="$t('Enter average class size')"
                              v-model.number="detailObj.avgClassSize"
                            />
                          </b-form-group>
                        </b-col>

                        <b-col md="6">
                          <b-form-group :label="$t('Monthly Fee Per Student')">
                            <b-form-input
                              type="number"
                              :placeholder="$t('Enter monthly fee per student')"
                              v-model.number="detailObj.monthlyFeePerStudent"
                            />
                          </b-form-group>
                        </b-col>

                        <b-col md="6">
                          <b-form-group
                            :label="$t('Monthly Fee For Head Office')"
                          >
                            <b-form-input
                              type="number"
                              :placeholder="
                                $t('Enter monthly fee for head office')
                              "
                              v-model.number="detailObj.monthlyFeeForHeadOffice"
                            />
                          </b-form-group>
                        </b-col>

                        <b-col md="6">
                          <b-form-group :label="$t('NTN')">
                            <b-form-input
                              :placeholder="$t('Enter NTN number')"
                              v-model="detailObj.ntn"
                            />
                          </b-form-group>
                        </b-col>
                      </b-row>
                    </b-form>
                  </b-tab>

                  <!-- ─────────────────────────────────────────
                     TAB 4 · Banking
                ───────────────────────────────────────── -->
                  <b-tab :title="$t('Banking')">
                    <b-form @submit.prevent>
                      <b-row>
                        <b-col md="6">
                          <b-form-group :label="$t('Bank Name')">
                            <b-form-input
                              :placeholder="$t('Enter bank name')"
                              v-model="detailObj.bankName"
                            />
                          </b-form-group>
                        </b-col>

                        <b-col md="6">
                          <b-form-group :label="$t('Bank Account Number')">
                            <b-form-input
                              :placeholder="$t('Enter account number')"
                              v-model="detailObj.bankAccountNumber"
                            />
                          </b-form-group>
                        </b-col>

                        <b-col md="6">
                          <b-form-group :label="$t('Bank Account Title')">
                            <b-form-input
                              :placeholder="$t('Enter account title')"
                              v-model="detailObj.bankAccountTitle"
                            />
                          </b-form-group>
                        </b-col>

                        <b-col md="6">
                          <b-form-group :label="$t('Bank Account Branch')">
                            <b-form-input
                              :placeholder="$t('Enter bank branch')"
                              v-model="detailObj.bankAccountBranch"
                            />
                          </b-form-group>
                        </b-col>

                        <b-col md="12">
                          <b-form-group :label="$t('Bank Details')">
                            <b-form-textarea
                              :placeholder="$t('Enter additional bank details')"
                              v-model="detailObj.bankDetails"
                              rows="3"
                            />
                          </b-form-group>
                        </b-col>

                        <!-- <b-col md="12">
                        <b-form-group :label="$t('Attachments')">
                          <b-form-input
                            :placeholder="$t('Attachment URL or reference')"
                            v-model="detailObj.attachments"
                          />
                        </b-form-group>
                      </b-col> -->
                      </b-row>
                    </b-form>
                  </b-tab>
                </b-tabs>

                <!-- Action buttons — always visible below tabs -->
                <b-row class="mt-2">
                  <b-col md="12" class="text-right">
                    <b-button
                      class="mr-1"
                      @click="backToGrid"
                      v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                      variant="outline-secondary"
                    >
                      <feather-icon icon="ChevronLeftIcon" />
                      <span class="align-middle"> {{ $t("Back") }}</span>
                    </b-button>
                    <b-button
                      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                      variant="success"
                      class="mr-1"
                      @click="save"
                      :disabled="request || bulkJobModalVisible"
                    >
                      <b-spinner
                        v-if="request"
                        small
                        variant="light"
                        label="Spinning"
                      />
                      <span v-else>{{ $t("Save") }}</span>
                    </b-button>
                  </b-col>
                </b-row>
              </b-card>
            </b-col>
            <b-col cols="2"></b-col>
          </b-row>
        </template>
      </b-container>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BFormTextarea,
  BFormCheckbox,
  BButton,
  BFormGroup,
  BForm,
  BContainer,
  BMediaAside,
  BImg,
  BMedia,
  BLink,
  BSpinner,
  BTabs,
  BTab,
  BModal,
  VBTooltip,
} from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import { mapActions } from "vuex";
import { getUploadPath, uploadToMySkool } from "@/utils/gdriveUpload";
import {
  CAMPUS_PROGRAM_OPTIONS,
  normalizeCampusProgramName,
} from "@/views/myskool/schoolinfo/campusProgramOptions";
import vSelect from "vue-select";
import {
  parseCompanyIds,
  formatCompanyIds,
} from "@/utils/companyIds";

function defaultCampus() {
  return {
    id: 0,
    name: "",
    branch: "",
    address: "",
    phone: "",
    mobile: "",
    email: "",
    website: "",
    logo: "",
    companyIDs: null,
    campusCode: "",
  };
}

function defaultDetailObj() {
  return {
    id: 0,
    campusID: 0,
    instituteName: "",
    branch: "",
    branchCode: "",
    yearOfEstablishment: 0,
    address: "",
    primaryContact: "",
    whatsappNumber: "",
    email: "",
    pocName: "",
    pocDesignation: "",
    pocContactNumber: "",
    pocEmail: "",
    pocAddress: "",
    pocCnic: "",
    pocPicture: "",
    programsOffered: "",
    studentCapacity: 0,
    avgClassSize: 0,
    monthlyFeePerStudent: 0,
    monthlyFeeForHeadOffice: 0,
    bankName: "",
    bankAccountNumber: "",
    bankAccountTitle: "",
    bankAccountBranch: "",
    bankDetails: "",
    ntn: "",
    attachments: "",
    status: "active",
    createdBy: 0,
    updatedBy: 0,
  };
}

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    BButton,
    BFormGroup,
    BForm,
    BContainer,
    BMediaAside,
    BImg,
    BMedia,
    BLink,
    BSpinner,
    BTabs,
    BTab,
    BModal,
    vSelect,
  },

  computed: {
    isIussDomain() {
      return String(this.$store.state.report || "")
        .replace(/_/g, "")
        .includes("iuss");
    },
    companySelectOptions() {
      return (this.companies || []).map((c) => ({
        id: c.id,
        label: [c.code, c.name].filter(Boolean).join(" — "),
      }));
    },
  },

  directives: {
    "b-tooltip": VBTooltip,
    Ripple,
  },

  async created() {
    if (this.isIussDomain) {
      await this.loadCompanies();
    }
    await this.initForm();
  },

  watch: {
    "$route.params.id"(newId, oldId) {
      if (newId === oldId) return;
      this.initForm();
    },
  },

  beforeDestroy() {
    this.stopBulkJobPolling();
    this.bulkJobModalVisible = false;
    this.request = false;
    this.bulkJobID = null;
  },

  data() {
    return {
      detailLoading: false,
      logoloading: false,
      pocPicLoading: false,
      request: false,
      dropOptions: {
        disabled: false,
        showDialCodeInList: true,
        showFlags: true,
      },

      programOptions: CAMPUS_PROGRAM_OPTIONS,
      selectedPrograms: [],
      othersProgram: "",

      limit: this.$store.state.branches[0].limit,

      whatsappError: false,
      primaryContactError: false,
      pocContactNumberError: false,

      campus: defaultCampus(),
      detailObj: defaultDetailObj(),

      bulkJobModalVisible: false,
      bulkJobID: null,
      bulkJobPollTimer: null,
      companies: [],
      selectedCompanyIds: [],
    };
  },

  methods: {
    ...mapActions({ get: "get", post: "post", put: "put" }),

    async loadCompanies() {
      try {
        const rows = await this.get({
          url:
            this.$store.state.domain +
            "Companies?db=" +
            encodeURIComponent(this.$store.state.userData.db) +
            "&cID=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        });
        this.companies = Array.isArray(rows) ? rows : [];
      } catch (e) {
        this.companies = [];
      }
    },
    syncCompanySelectionFromCampus() {
      this.selectedCompanyIds = parseCompanyIds(this.campus.companyIDs);
    },
    applyCompanyIdsBeforeSave() {
      if (!this.isIussDomain) return;
      this.campus.companyIDs = formatCompanyIds(this.selectedCompanyIds);
    },

    // ── Logo upload ────────────────────────────────────────
    deleteLogo() {
      this.campus.logo = "";
      this.logoloading = false;
    },

    async selectLogo() {
      const file = this.$refs.fileProfile.files[0];
      if (!file) return;
      this.logoloading = true;
      try {
        const uploaded = await uploadToMySkool([file]);
        this.campus.logo = getUploadPath(uploaded[0]);
        this.logoloading = "loaded";
      } catch (err) {
        console.log(err);
        this.logoloading = false;
      }
    },

    // ── POC Picture upload ─────────────────────────────────
    async uploadPocPic() {
      const file = this.$refs.pocPicInput.files[0];
      if (!file) return;
      this.pocPicLoading = true;
      try {
        const uploaded = await uploadToMySkool([file]);
        this.detailObj.pocPicture = getUploadPath(uploaded[0]);
      } catch (err) {
        console.log(err);
      } finally {
        this.pocPicLoading = false;
      }
    },

    // ── Validation ─────────────────────────────────────────
    checkField(ref) {
      const elem = this.$refs[ref];
      if (!elem) return false;
      const val = ref === "website" ? this.campus.website : this.detailObj[ref];
      const valid = val !== undefined && val;
      elem.state = valid ? true : false;
      return valid;
    },

    checkEmail() {
      const elem = this.$refs["email"];
      const re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      const valid =
        re.test(String(this.detailObj.email).toLowerCase()) &&
        this.detailObj.email !== "";
      elem.state = valid ? true : false;
      return valid;
    },

    checkWhatsappNumber(_, e) {
      if (this.detailObj.whatsappNumber) {
        if (e.valid) {
          this.whatsappError = false;
          this.detailObj.whatsappNumber = e.number;
        } else {
          this.whatsappError = true;
        }
      } else {
        this.whatsappError = false;
      }
    },

    checkPrimaryContact(_, e) {
      if (this.detailObj.primaryContact) {
        if (e.valid) {
          this.primaryContactError = false;
          this.detailObj.primaryContact = e.number;
        } else {
          this.primaryContactError = true;
        }
      } else {
        this.primaryContactError = false;
      }
    },

    checkPocContactNumber(_, e) {
      if (this.detailObj.pocContactNumber) {
        if (e.valid) {
          this.pocContactNumberError = false;
          this.detailObj.pocContactNumber = e.number;
        } else {
          this.pocContactNumberError = true;
        }
      } else {
        this.pocContactNumberError = false;
      }
    },

    isNumericFieldEntered(val) {
      if (val === null || val === undefined) return false;
      if (typeof val === "string" && val.trim() === "") return false;
      return true;
    },

    validateOptionalNumbers() {
      const fields = [
        { key: "yearOfEstablishment", label: this.$t("Year of Establishment") },
        { key: "studentCapacity", label: this.$t("Student Capacity") },
        { key: "avgClassSize", label: this.$t("Avg Class Size") },
        {
          key: "monthlyFeePerStudent",
          label: this.$t("Monthly Fee Per Student"),
        },
        {
          key: "monthlyFeeForHeadOffice",
          label: this.$t("Monthly Fee For Head Office"),
        },
      ];
      for (let i = 0; i < fields.length; i++) {
        const f = fields[i];
        const raw = this.detailObj[f.key];
        if (!raw) continue;
        const n = Number(raw);
        if (Number.isNaN(n) || n < 0) {
          this.$bvToast.toast(
            this.$t("Please enter a valid number for") + " " + f.label + ".",
            {
              title: this.$t("Error!"),
              variant: "danger",
              toaster: "b-toaster-top-center",
            }
          );
          return false;
        }
      }
      return true;
    },

    toNumOrZero(val) {
      if (val === "" || val === null || val === undefined) return 0;
      const n = Number(val);
      return Number.isNaN(n) ? 0 : n;
    },

    normalizeNumericDetailFields() {
      const keys = [
        "yearOfEstablishment",
        "studentCapacity",
        "avgClassSize",
        "monthlyFeePerStudent",
        "monthlyFeeForHeadOffice",
      ];
      keys.forEach((k) => {
        this.detailObj[k] = this.toNumOrZero(this.detailObj[k]);
      });
    },

    // ── programsOffered helpers ────────────────────────────
    serializePrograms() {
      const parts = [...this.selectedPrograms];
      if (this.othersProgram.trim() !== "") {
        parts.push(this.othersProgram.trim());
      }
      this.detailObj.programsOffered = parts.join(", ");
    },

    validateProgramsOffered() {
      const hasCheckbox =
        Array.isArray(this.selectedPrograms) &&
        this.selectedPrograms.length > 0;
      const hasOthers = (this.othersProgram || "").trim() !== "";
      if (!hasCheckbox && !hasOthers) {
        this.$bvToast.toast(
          this.$t("Please select at least one program or specify others."),
          {
            title: this.$t("Error!"),
            variant: "danger",
            toaster: "b-toaster-top-center",
          }
        );
        return false;
      }
      return true;
    },

    deserializePrograms(val) {
      if (!val) return;
      const all = val
        .split(",")
        .map((s) => normalizeCampusProgramName(s))
        .filter(Boolean);
      this.selectedPrograms = all.filter((p) =>
        this.programOptions.includes(p)
      );
      const others = all.filter((p) => !this.programOptions.includes(p));
      this.othersProgram = others.join(", ");
    },

    async fetchCampusDetails(id) {
      this.detailLoading = true;
      const res = await this.get({
        url:
          this.$store.state.domain +
          "CampusDetails/GetByCampusID?db=" +
          this.$store.state.userData.db +
          "&campusID=" +
          id,
        token: this.$store.state.userData.token,
        context: this,
      });
      this.detailLoading = false;

      const c = res.campus ?? res.campus;
      const d = res.detail ?? res.detail;
      // console.log("c", c);
      // console.log("d", d);

      if (!c && !d) {
        this.$bvToast.toast(this.$t("Invalid campus data received."), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        this.backToGrid();
        return;
      }

      this.campus = c || defaultCampus();
      this.detailObj = d || defaultDetailObj();
      this.syncCompanySelectionFromCampus();
      this.deserializePrograms(this.detailObj.programsOffered || "");
      if (this.campus.logo) {
        this.logoloading = "loaded";
      } else {
        this.logoloading = false;
      }
      this.whatsappError = false;
      this.primaryContactError = false;
      this.pocContactNumberError = false;
    },

    async initForm() {
      const rawId = this.$route.params.id;
      const idNum =
        rawId !== undefined && rawId !== null && rawId !== ""
          ? Number(rawId)
          : NaN;

      if (!Number.isNaN(idNum) && idNum > 0) {
        await this.fetchCampusDetails(idNum);
        return;
      }

      this.campus = defaultCampus();
      this.detailObj = defaultDetailObj();
      this.syncCompanySelectionFromCampus();
      this.whatsappError = false;
      this.primaryContactError = false;
      this.pocContactNumberError = false;

      // console.log("this.campus", this.campus);
      // console.log("this.detailObj", this.detailObj);
      this.selectedPrograms = [];
      this.othersProgram = "";
      this.logoloading = false;
    },

    // ── Save ───────────────────────────────────────────────
    async save() {
      // if (
      //   this.campus.id === 0 &&
      //   this.limit === this.$store.state.branches.length
      // ) {
      //   this.$bvToast.toast(
      //     this.$t("You have reached your limit. Please Contact MySkool Team."),
      //     {
      //       title: this.$t("Error!"),
      //       variant: "danger",
      //       toaster: "b-toaster-top-center",
      //     }
      //   );
      //   return;
      // }

      if (!this.detailObj.primaryContact) this.primaryContactError = true;
      if (!this.detailObj.whatsappNumber) this.whatsappError = true;
      if (!this.detailObj.pocContactNumber) this.pocContactNumberError = true;

      if (
        !this.checkField("instituteName") ||
        !this.checkField("branch") ||
        !this.checkField("branchCode") ||
        !this.checkField("pocName") ||
        !this.checkEmail() ||
        this.primaryContactError ||
        this.whatsappError ||
        this.pocContactNumberError
      ) {
        this.$bvToast.toast(
          this.$t("Please fill the required fields correctly."),
          {
            title: this.$t("Error!"),
            variant: "danger",
            toaster: "b-toaster-top-center",
          }
        );
        return;
      }

      if (!this.validateOptionalNumbers()) return;
      if (!this.validateProgramsOffered()) return;

      this.normalizeNumericDetailFields();
      this.serializePrograms();
      this.campus.name = this.detailObj.instituteName;
      this.campus.branch = this.detailObj.branch;
      this.applyCompanyIdsBeforeSave();

      this.request = true;

      if (this.campus.id === 0) {
        this.bulkJobModalVisible = true;
        await this.submitNewCampusViaBulkUpload();
        return;
      } else {
        const status = await this.post({
          url:
            this.$store.state.domain +
            "CampusDetails/UpdateNew?db=" +
            this.$store.state.userData.db +
            "&userID=" +
            this.$store.state.userData.userID,
          body: { campus: this.campus, detail: this.detailObj },
          message: this.$t("Campus updated successfully."),
          token: this.$store.state.userData.token,
          context: this,
          showError: true,
        });
        this.request = false;
        if (status) this.backToGrid();
      }
    },

    stopBulkJobPolling() {
      if (this.bulkJobPollTimer) {
        clearInterval(this.bulkJobPollTimer);
        this.bulkJobPollTimer = null;
      }
    },

    finishBulkJobUi() {
      this.bulkJobModalVisible = false;
      this.request = false;
      this.stopBulkJobPolling();
      this.bulkJobID = null;
    },

    async submitNewCampusViaBulkUpload() {
      const response = await this.post({
        url:
          this.$store.state.domain +
          "CampusDetails/BulkUpload?db=" +
          this.$store.state.userData.db +
          "&userID=" +
          this.$store.state.userData.userID,
        body: [this.detailObj],
        context: this,
        isBulkUpload: true,
        token: this.$store.state.userData.token,
      });

      if (!response) {
        this.$bvToast.toast(this.$t("Upload failed."), {
          title: this.$t("Error") + "!",
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        this.finishBulkJobUi();
        return;
      }

      if (response.status === "error") {
        let invalid = 0;
        if (Array.isArray(response.data)) {
          invalid = response.data.length;
        }
        const baseMsg = this.$t("Validation failed.");
        const msg =
          invalid > 0
            ? `${baseMsg} ${invalid} ${this.$t("invalid records")}`
            : baseMsg;

        this.$bvToast.toast(msg, {
          title: this.$t("Error") + "!",
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        this.finishBulkJobUi();
        return;
      }

      const jid =
        response.jobID ||
        response.jobId ||
        (response.data && (response.data.jobID || response.data.jobId));

      if (!jid) {
        this.$bvToast.toast(this.$t("Upload failed."), {
          title: this.$t("Error") + "!",
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        this.finishBulkJobUi();
        return;
      }

      this.bulkJobID = jid;
      await this.startBulkJobPolling();
    },

    async startBulkJobPolling() {
      this.stopBulkJobPolling();
      await this.pollBulkJobStatus();
      this.bulkJobPollTimer = setInterval(() => {
        this.pollBulkJobStatus();
      }, 2000);
    },

    async pollBulkJobStatus() {
      if (!this.bulkJobID) return;
      const res = await this.get({
        url:
          this.$store.state.domain +
          "BackgroundJobs/GetJobStatus/" +
          this.bulkJobID +
          "?db=" +
          this.$store.state.userData.db,
        token: this.$store.state.userData.token,
        returnData: true,
        context: this,
      });

      if (res === false || res == null) {
        this.$bvToast.toast(this.$t("Upload failed."), {
          title: this.$t("Error") + "!",
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        this.finishBulkJobUi();
        return;
      }

      if (res.status === "error" || res.status === "failed") {
        this.$bvToast.toast(this.$t("Upload failed."), {
          title: this.$t("Error") + "!",
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        this.finishBulkJobUi();
        return;
      }

      if (res.status === "notFound") {
        this.$bvToast.toast(this.$t("Upload failed."), {
          title: this.$t("Error") + "!",
          variant: "warning",
          toaster: "b-toaster-top-center",
        });
        this.finishBulkJobUi();
        return;
      }

      const pct = Number(res.percent);
      const total = Number(res.total);
      const processed = Number(res.processed);
      const done =
        res.status == "succeeded" ||
        (!Number.isNaN(pct) && pct >= 100) ||
        (total > 0 && !Number.isNaN(processed) && processed >= total);

      if (done) {
        this.$bvToast.toast(this.$t("Campus added successfully."), {
          title: this.$t("Success"),
          variant: "success",
          toaster: "b-toaster-top-center",
        });
        this.finishBulkJobUi();
        this.backToGrid();
      }
    },

    backToGrid() {
      this.$router.go(-1);
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
